import React, { useMemo } from "react";
import { AlertTriangle, Repeat, SkipForward } from "lucide-react";
import { NewTask, RecurrenceFrequency, Task } from "../../../types";
import {
  CLEARED_RECURRENCE,
  countMissedOccurrences,
  getFirstOccurrenceFromToday,
  getNextDayOfMonthFromToday,
  getNextMonthDayFromToday,
  getNextOccurrenceDate,
  getRecurrenceLabel,
  ruleFromTask,
} from "../../../lib/recurrence";
import { useSkipRecurringOccurrence } from "../../../hooks/mutations/useSkipRecurringOccurrence";

export interface RecurrenceConfigProps {
  /**
   * The task being edited. Every control here renders straight off it — there
   * is no draft copy of the rule; edits go out through `onSchedulingChange`
   * and come back as new props.
   */
  task: Task | NewTask;
  /**
   * Edit mode surfaces the catch-up button. Add mode has no persisted task to
   * advance, so it never renders one.
   */
  mode: "add" | "edit";
  /**
   * Commit one or more scheduling fields. Several controls write more than one
   * at once — picking a frequency also re-anchors the date, for instance — and
   * the caller's auto-save gives the whole cluster a longer debounce window.
   */
  onSchedulingChange: (updates: Partial<Task>) => void;
  /** Carry over is on. Mutually exclusive with recurrence. */
  persistentActive: boolean;
  /** Working hours only is on. Also mutually exclusive with recurrence. */
  workHoursActive: boolean;
  /**
   * The time picker and duration controls. They live in this section while
   * recurrence is on (the rule replaces the single date pick) and in the
   * Schedule section otherwise, so the dialog owns them and lends them here.
   */
  timeAndDurationFields: React.ReactNode;
  /**
   * Commit the dialog's debounced auto-save right now. The catch-up button has
   * to await this before it skips: the skip writes `scheduled_date` itself and
   * resolves the task it is skipping from the query cache, so an edit still
   * sitting in the debounce window would be both invisible to it and written
   * back over it afterwards.
   */
  onFlushPendingSave: () => Promise<boolean>;
  /** Close the dialog — skipping to the next occurrence navigates away. */
  onClose: () => void;
}

/**
 * The Repeat toggle and everything underneath it: the all-day/timed pills, the
 * frequency presets, the per-frequency anchor pickers, the custom interval, the
 * next-occurrence readout and the catch-up button for missed occurrences.
 *
 * Renders nothing until the task has a scheduled date — a rule needs an anchor
 * to repeat from.
 */
const RecurrenceConfig: React.FC<RecurrenceConfigProps> = ({
  task,
  mode,
  onSchedulingChange,
  persistentActive,
  workHoursActive,
  timeAndDurationFields,
  onFlushPendingSave,
  onClose,
}) => {
  // Skipping missed recurring occurrences. Called here rather than threaded in
  // as a prop — the catch-up button below is the only caller.
  const { skipRecurringOccurrence } = useSkipRecurringOccurrence();

  // How many recurrence fire days have passed without the task being completed.
  // Drives whether we surface the catch-up "Skip to next occurrence" button.
  const missedRecurringOccurrences = useMemo(() => {
    if (!task.recurrence_frequency || !task.scheduled_date) return 0;
    const rule = ruleFromTask({
      recurrence_frequency: task.recurrence_frequency,
      recurrence_interval: task.recurrence_interval ?? 1,
      recurrence_day_of_week: task.recurrence_day_of_week ?? null,
      recurrence_end_date: task.recurrence_end_date ?? null,
    });
    if (!rule) return 0;
    return countMissedOccurrences(task.scheduled_date, rule);
  }, [
    task.recurrence_frequency,
    task.recurrence_interval,
    task.recurrence_day_of_week,
    task.recurrence_end_date,
    task.scheduled_date,
  ]);

  // Where the rule actually fires next. `scheduled_date` answers that only
  // while it is still ahead of today — once occurrences have been missed it is
  // the date of the *oldest* missed one, so reading it out as "Next" would
  // advertise a date in the past exactly when the section is telling the user
  // they've fallen behind. Null means the rule's end date has been passed and
  // there is no next occurrence at all.
  const nextOccurrenceDate = useMemo(() => {
    if (!task.scheduled_date) return null;
    if (missedRecurringOccurrences === 0) return task.scheduled_date;
    const rule = ruleFromTask({
      recurrence_frequency: task.recurrence_frequency ?? null,
      recurrence_interval: task.recurrence_interval ?? 1,
      recurrence_day_of_week: task.recurrence_day_of_week ?? null,
      recurrence_end_date: task.recurrence_end_date ?? null,
    });
    if (!rule) return task.scheduled_date;
    return getNextOccurrenceDate(task.scheduled_date, rule);
  }, [
    missedRecurringOccurrences,
    task.recurrence_frequency,
    task.recurrence_interval,
    task.recurrence_day_of_week,
    task.recurrence_end_date,
    task.scheduled_date,
  ]);

  // Catch up on missed occurrences. The flush has to settle before the skip
  // starts: it persists whatever the user just changed *and* refreshes the
  // query cache the skip reads its rule from, so the two can't disagree about
  // which date is advanced or by how much. Without it a rule edit made in the
  // debounce window would be skipped from stale values, then written back over
  // by the unmount flush — and a flush that failed leaves only those values.
  const handleSkipToNextOccurrence = async () => {
    if (!(await onFlushPendingSave())) return;
    void skipRecurringOccurrence((task as Task).id);
    onClose();
  };

  // Carry over and Recurrence are mutually exclusive. Working hours only
  // is also mutually exclusive with Recurrence (but combines freely with
  // Carry over — that pairing is the whole point of the work-hours flag for
  // persistent tasks). We disable the toggle while it is OFF and an exclusive
  // partner is ON, so the user has to consciously turn the active one off
  // before enabling this one (rather than silently flipping it via the prior
  // auto-clear behaviour).
  const recurrenceDisabled =
    !task.recurrence_frequency && (persistentActive || workHoursActive);
  const recurrenceDisabledReason = persistentActive
    ? "Turn off Carry over to use this"
    : "Turn off Working hours only to use this";

  if (!task.scheduled_date) return null;

  // Same value as `task.scheduled_date`, but narrowed to a string that survives
  // into the click handlers below — TypeScript drops the narrowing above the
  // moment a property read crosses into a callback.
  const anchorDate = task.scheduled_date;

  return (
    <div className="space-y-2">
      <button
        type="button"
        role="switch"
        aria-checked={!!task.recurrence_frequency}
        disabled={recurrenceDisabled}
        onClick={() => {
          if (task.recurrence_frequency) {
            // Turning recurrence off clears the rule but leaves
            // scheduled_date where it is — turning Repeat on may
            // have moved a past-dated task forward, and that
            // reschedule stands on its own, the same way turning
            // the toggle back off doesn't undo any other field.
            onSchedulingChange({ ...CLEARED_RECURRENCE });
          } else {
            // Turn on recurrence. Anchor scheduled_date to the
            // existing future date, or to the rule's first
            // occurrence from today when it's already past.
            const scheduledDay = task.scheduled_date
              ? new Date(task.scheduled_date + "T00:00:00").getDay()
              : new Date().getDay();
            const newRule = {
              frequency: "weekly" as RecurrenceFrequency,
              interval: 1,
              dayOfWeek: scheduledDay,
              endDate: null,
            };
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
            const anchor = task.scheduled_date && task.scheduled_date >= todayStr
              ? task.scheduled_date
              : getFirstOccurrenceFromToday(newRule);
            onSchedulingChange({
              recurrence_frequency: newRule.frequency,
              recurrence_interval: newRule.interval,
              recurrence_day_of_week: newRule.dayOfWeek,
              recurrence_end_date: null,
              persistent: false,
              scheduled_date: anchor,
            });
          }
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-soft text-sm transition-all duration-150 ease-in-out text-left disabled:opacity-50 disabled:cursor-not-allowed ${
          task.recurrence_frequency
            ? "bg-warm-blue/10 border border-warm-blue/30"
            : recurrenceDisabled
              ? "bg-warm-bg-elevated border border-warm-border"
              : "bg-warm-bg-elevated border border-warm-border hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
        }`}
      >
        <Repeat
          size={16}
          className={
            task.recurrence_frequency
              ? "text-warm-blue flex-shrink-0"
              : "text-warm-text-tertiary flex-shrink-0"
          }
        />
        <div className="flex-1 min-w-0">
          <div
            className={
              task.recurrence_frequency
                ? "font-medium text-warm-blue"
                : "text-warm-text-secondary"
            }
          >
            {task.recurrence_frequency
              ? getRecurrenceLabel({
                  recurrence_frequency: task.recurrence_frequency,
                  recurrence_interval: task.recurrence_interval ?? 1,
                  recurrence_day_of_week: task.recurrence_day_of_week ?? null,
                  scheduled_date: task.scheduled_date,
                })
              : "Repeat"}
          </div>
          <div className="text-[11px] text-warm-text-tertiary leading-tight mt-0.5">
            {task.recurrence_frequency
              ? "Reschedules to next occurrence when completed"
              : recurrenceDisabled
                ? recurrenceDisabledReason
                : "Repeats on a fixed schedule"}
          </div>
        </div>
        <div
          className={`relative w-9 h-5 rounded-full transition-colors duration-150 ease-in-out flex-shrink-0 ${
            task.recurrence_frequency
              ? "bg-warm-blue"
              : "bg-warm-border"
          }`}
        >
          <div
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-150 ease-in-out ${
              task.recurrence_frequency
                ? "translate-x-4"
                : "translate-x-0"
            }`}
          />
        </div>
      </button>

      {/* Recurrence options, indented under the toggle they
          belong to. Every control here saves on its own like any
          other field — see SCHEDULING_FIELDS for why the window
          is longer than the rest of the dialog's. */}
      {task.recurrence_frequency && (
        <div className="space-y-3 ml-4 pl-3 border-l-2 border-warm-blue/30">
          {/* Heads-up: edits made on Google Calendar's side don't sync back */}
          <div className="flex items-start gap-1.5 text-[11px] text-warm-text-tertiary leading-snug">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-warm-blue/70" />
            <span>Recurrence syncs one way to Google Calendar — edits made there won't be reflected in JuDI.</span>
          </div>

          {/* All-day vs Timed (time + duration live here when recurring,
              since the recurrence rule replaces a single date pick). */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { id: "allday" as const, label: "All day", active: !task.scheduled_time },
              { id: "timed" as const, label: "At a time", active: !!task.scheduled_time },
            ].map(({ id, label, active }) => (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  if (id === "allday") {
                    onSchedulingChange({ scheduled_time: null, duration: null });
                  } else if (!task.scheduled_time) {
                    onSchedulingChange({ scheduled_time: "09:00" });
                  }
                }}
                className={`px-2.5 py-1 rounded-soft text-xs transition-all duration-150 ease-in-out ${
                  active
                    ? "bg-warm-blue text-white"
                    : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Time + duration (if timed) — lent by the dialog, which shows the
              same controls in the Schedule section when recurrence is off. */}
          {task.scheduled_time && timeAndDurationFields}

          {/* Frequency presets */}
          <div className="flex flex-wrap gap-1.5">
            {(["daily", "weekly", "monthly", "yearly"] as RecurrenceFrequency[]).map((freq) => (
              <button
                key={freq}
                type="button"
                aria-pressed={task.recurrence_frequency === freq}
                onClick={() => {
                  // These buttons pick the frequency and nothing else — "every
                  // N" belongs to the interval input below. Re-selecting the
                  // frequency the rule already has is therefore a no-op rather
                  // than a reset: it used to drop the interval back to 1 and
                  // re-anchor the date, silently rewriting "every 2 months on
                  // the 5th" into "monthly on today's day-of-month".
                  if (task.recurrence_frequency === freq) return;
                  const anchorDay = new Date(anchorDate + "T00:00:00").getDay();
                  // Weekly needs a weekday; the first time a rule becomes
                  // weekly it inherits the anchor's. Other frequencies leave
                  // the column alone instead of nulling it, so weekly →
                  // monthly → weekly returns to the day the user chose.
                  const dayOfWeek =
                    freq === "weekly"
                      ? (task.recurrence_day_of_week ?? anchorDay)
                      : (task.recurrence_day_of_week ?? null);
                  const newRule = {
                    frequency: freq as RecurrenceFrequency,
                    interval: task.recurrence_interval ?? 1,
                    dayOfWeek,
                    endDate: task.recurrence_end_date ?? null,
                  };
                  onSchedulingChange({
                    recurrence_frequency: newRule.frequency,
                    recurrence_day_of_week: newRule.dayOfWeek,
                    scheduled_date: getFirstOccurrenceFromToday(newRule),
                  });
                }}
                className={`px-2.5 py-1 rounded-soft text-xs transition-all duration-150 ease-in-out ${
                  task.recurrence_frequency === freq
                    ? "bg-warm-blue text-white"
                    : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
                }`}
              >
                {freq.charAt(0).toUpperCase() + freq.slice(1)}
              </button>
            ))}
          </div>

          {/* Day of week picker for weekly recurrence */}
          {task.recurrence_frequency === "weekly" && (
            <div className="flex gap-1">
              {[
                ["S", "Sunday"],
                ["M", "Monday"],
                ["T", "Tuesday"],
                ["W", "Wednesday"],
                ["T", "Thursday"],
                ["F", "Friday"],
                ["S", "Saturday"],
              ].map(([dayLabel, dayName], dayIndex) => (
                <button
                  key={dayIndex}
                  type="button"
                  aria-label={dayName}
                  aria-pressed={(task.recurrence_day_of_week ?? -1) === dayIndex}
                  onClick={() => {
                    const newRule = {
                      frequency: "weekly" as RecurrenceFrequency,
                      interval: task.recurrence_interval ?? 1,
                      dayOfWeek: dayIndex,
                      endDate: task.recurrence_end_date ?? null,
                    };
                    onSchedulingChange({
                      recurrence_day_of_week: dayIndex,
                      scheduled_date: getFirstOccurrenceFromToday(newRule),
                    });
                  }}
                  className={`w-8 h-8 rounded-full text-xs font-medium transition-all duration-150 ease-in-out ${
                    (task.recurrence_day_of_week ?? -1) === dayIndex
                      ? "bg-warm-blue text-white"
                      : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
                  }`}
                >
                  {dayLabel}
                </button>
              ))}
            </div>
          )}

          {/* Day of month picker for monthly recurrence */}
          {task.recurrence_frequency === "monthly" && task.scheduled_date && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-warm-text-tertiary">Day of month</span>
              <input
                type="number"
                aria-label="Day of month"
                min={1}
                max={31}
                value={new Date(task.scheduled_date + "T00:00:00").getDate()}
                onChange={(e) => {
                  const day = Math.max(1, Math.min(31, parseInt(e.target.value) || 1));
                  onSchedulingChange({ scheduled_date: getNextDayOfMonthFromToday(day) });
                }}
                className="w-14 bg-warm-bg-elevated border border-warm-border rounded-soft px-2 py-1 text-xs text-warm-text-primary text-center focus:outline-none focus:ring-2 focus:ring-warm-blue"
              />
              <span className="text-[11px] text-warm-text-tertiary">
                Days beyond a month's length use the last day (e.g. 31 → Feb 28).
              </span>
            </div>
          )}

          {/* Month + day picker for yearly recurrence */}
          {task.recurrence_frequency === "yearly" && task.scheduled_date && (() => {
            const anchor = new Date(task.scheduled_date + "T00:00:00");
            const monthIdx = anchor.getMonth();
            const dayOfMonth = anchor.getDate();
            const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
            return (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-warm-text-tertiary">On</span>
                <select
                  aria-label="Recurrence month"
                  value={monthIdx}
                  onChange={(e) => {
                    const m = parseInt(e.target.value, 10);
                    onSchedulingChange({ scheduled_date: getNextMonthDayFromToday(m, dayOfMonth) });
                  }}
                  className="bg-warm-bg-elevated border border-warm-border rounded-soft px-2 py-1 text-xs text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
                >
                  {monthNames.map((name, idx) => (
                    <option key={idx} value={idx}>{name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  aria-label="Recurrence day of month"
                  min={1}
                  max={31}
                  value={dayOfMonth}
                  onChange={(e) => {
                    const day = Math.max(1, Math.min(31, parseInt(e.target.value) || 1));
                    onSchedulingChange({ scheduled_date: getNextMonthDayFromToday(monthIdx, day) });
                  }}
                  className="w-14 bg-warm-bg-elevated border border-warm-border rounded-soft px-2 py-1 text-xs text-warm-text-primary text-center focus:outline-none focus:ring-2 focus:ring-warm-blue"
                />
              </div>
            );
          })()}

          {/* Custom interval */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-warm-text-tertiary">Every</span>
            <input
              type="number"
              aria-label="Recurrence interval"
              min={1}
              max={99}
              value={task.recurrence_interval ?? 1}
              onChange={(e) => {
                const val = Math.max(1, Math.min(99, parseInt(e.target.value) || 1));
                onSchedulingChange({ recurrence_interval: val });
              }}
              className="w-14 bg-warm-bg-elevated border border-warm-border rounded-soft px-2 py-1 text-xs text-warm-text-primary text-center focus:outline-none focus:ring-2 focus:ring-warm-blue"
            />
            <span className="text-xs text-warm-text-tertiary">
              {task.recurrence_frequency === "daily" && ((task.recurrence_interval ?? 1) === 1 ? "day" : "days")}
              {task.recurrence_frequency === "weekly" && ((task.recurrence_interval ?? 1) === 1 ? "week" : "weeks")}
              {task.recurrence_frequency === "monthly" && ((task.recurrence_interval ?? 1) === 1 ? "month" : "months")}
              {task.recurrence_frequency === "yearly" && ((task.recurrence_interval ?? 1) === 1 ? "year" : "years")}
            </span>
          </div>

          {/* Next-occurrence indicator — the recurrence section hides
              the date input, so this is the user's only signal of
              when the next occurrence actually falls. */}
          <div className="text-[11px] text-warm-text-tertiary leading-snug">
            {nextOccurrenceDate ? (
              <>
                Next: <span className="font-medium text-warm-text-secondary">
                  {new Date(nextOccurrenceDate + "T00:00:00").toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </>
            ) : (
              "No occurrences left — the repeat's end date has passed."
            )}
          </div>

          {/* Catch-up button — only when one or more occurrences are overdue */}
          {mode === "edit" && missedRecurringOccurrences > 0 && (
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => void handleSkipToNextOccurrence()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-warm-text-secondary bg-warm-bg-elevated border border-warm-border rounded-soft hover:border-warm-blue/50 dark:hover:border-warm-blue/50 transition-all duration-150 ease-in-out"
              >
                <SkipForward size={14} />
                Skip to next occurrence
              </button>
              <p className="text-[11px] text-warm-text-tertiary leading-snug">
                {missedRecurringOccurrences === 1
                  ? "You missed the last occurrence. Click to skip it and move to the next one."
                  : `You missed the last ${missedRecurringOccurrences} occurrences. Click to skip them and move to the next one.`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecurrenceConfig;
